import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, StopCircle } from "lucide-react";
import { api } from "@/lib/api";
import { ANSWER_LABEL, fmtDateTime } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { PhotoThumbs } from "@/components/fleet/Photos";
import { HandoffNote } from "@/components/fleet/BikeCards";

const StageBlock = ({ title, data }) => (
  <section className="overflow-hidden rounded-2xl border bg-card" data-testid={`run-stage-${title.toLowerCase().replace(/ /g, "-")}`}>
    <div className="flex items-center justify-between border-b px-5 py-3">
      <h3 className="font-bold">{title}</h3>
      <span className="text-xs text-muted-foreground">{fmtDateTime(data.at)} · <span className="font-mono">{data.mileage} mi</span></span>
    </div>
    {data.results.map((r) => (
      <div key={r.item_id} className={`space-y-2 border-b px-5 py-2.5 text-sm last:border-0 ${r.issue ? "bg-red-500/5" : ""}`}>
        <div className="flex justify-between gap-3">
          <span>{r.label}</span>
          <span className={`font-semibold ${r.issue ? "text-red-400" : r.answer === "not_done" ? "text-zinc-400" : "text-emerald-400"}`}>{ANSWER_LABEL[r.answer]}</span>
        </div>
        {r.note && <p className="text-xs text-muted-foreground">{r.note}</p>}
        {r.photos?.length > 0 && <PhotoThumbs photos={r.photos} />}
      </div>
    ))}
    {data.general_notes && <p className="border-t px-5 py-3 text-sm text-muted-foreground">{data.general_notes}</p>}
  </section>
);

export default function RunDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [run, setRun] = useState(null);
  useEffect(() => { api.get(`/runs/${id}`).then((r) => setRun(r.data)); }, [id]);
  if (!run) return <div className="h-40 animate-pulse rounded-2xl bg-card" />;
  const canFinish = run.status === "out" && (run.rider_user_id === user.id || ["admin", "fleet_manager"].includes(user.role));

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Link to="/fleet" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Fleet</Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 data-testid="run-title" className="text-3xl font-extrabold tracking-tight">{run.bike_name}</h1>
          <p className="text-sm text-muted-foreground">{run.rider_name} · {run.status === "out" ? "Out on run" : `Completed · ${run.miles} miles`} · {run.fault_count} issue{run.fault_count === 1 ? "" : "s"}</p>
        </div>
        {canFinish && <Link to={`/bike-check/${run.id}/post`} data-testid="run-post-check-btn" className="inline-flex items-center gap-2 rounded-full bg-destructive px-6 py-3 text-sm font-bold text-white hover:bg-red-700"><StopCircle className="h-4 w-4" /> Do post-ride check</Link>}
      </div>
      {run.handoff_note && <HandoffNote note={run.handoff_note} testid="run-handoff-note" />}
      <StageBlock title="Pre-ride check" data={run.pre} />
      {run.post && <StageBlock title="Post-ride check" data={run.post} />}
    </div>
  );
}
